import { v4 } from 'uuid';

import { ICardProcessingEnvironmentFromServer } from '@crm-framework/dashboard';

import { IAppConfigModel } from '@crm-package/app';

export const createFavicon = (url: string): void => {
  const link = document.createElement('link');
  link.rel = 'icon';
  link.href = url;

  document.querySelector('head')!.appendChild(link);
};

export function camelize(str: string): string {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
      index === 0 ? word.toLowerCase() : word.toUpperCase()
    )
    .replace(/\s+/g, '');
}

export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function compareValues<
  T extends { [TKey in TKeys]: string },
  TKeys extends keyof T
>(key: TKeys, order: 'asc' | 'desc' = 'asc') {
  return function innerSort(a: T, b: T) {
    const uppercaseA = a[key];
    const uppercaseB = b[key];
    let comparison = 0;

    if (uppercaseA > uppercaseB) {
      comparison = 1;
    } else if (uppercaseA < uppercaseB) {
      comparison = -1;
    }

    return order === 'desc' ? comparison * -1 : comparison;
  };
}

export function convertResponseHeadersToObject(
  responseHeaders: Headers
): Record<string, string> {
  const headers = {};

  responseHeaders.forEach((value: string, key: string) => {
    headers[key] = value;
  });

  return headers;
}

export function ensure<T>(
  value: T | undefined | null,
  message: string = 'This value was promised to be there.'
): T {
  if (value === undefined || value === null) {
    throw new TypeError(message);
  }

  return value;
}

export const objFilter = <TFilterObj = Record<string | number | symbol, any>>(
  obj: Readonly<TFilterObj>,
  callback: (value: any, key: string | number | symbol) => boolean
): Partial<TFilterObj> => {
  const keys = Object.keys(obj);

  return keys.reduce((result, key) => {
    const value = obj[key];

    if (callback(value, key)) {
      result[key] = value;
    }

    return result;
  }, {});
};

export const makeArrayWithUid = (amount: number): string[] =>
  new Array(amount).fill(0).map(() => v4());

export const mapToPluralForm = (count: number, subject: string): string =>
  count > 1 ? `${count} ${subject}s` : `${count} ${subject}`;

export const getLoadingRegistry = (
  componentName: string,
  item: string | number
) => `${componentName}/${item}-loading`;

export interface IBaseUrlConfig {
  page?: string;
  // TODO delete fragment after all components redesign
  fragment?: string;
  path?: string;
  buId?: number;
  query?: {
    [key: string]: unknown;
  };
}

interface ICreatePmntBaseUrl {
  urlConfig: IBaseUrlConfig;
  crmConfig?: IAppConfigModel | null;
  cardProcessingEnvironments?: ICardProcessingEnvironmentFromServer[];
}
// TODO: Maybe transition to other folder
export const createPmntBaseUrl = ({
  urlConfig,
  crmConfig,
  cardProcessingEnvironments,
}: ICreatePmntBaseUrl) => {
  const businessUnitId = urlConfig.buId;
  let query = urlConfig.query || {};
  const page = urlConfig.page || '';

  // TODO delete fragment after all components redesign
  const fragment = urlConfig.fragment || '';
  const path = urlConfig.path || '';

  if (!crmConfig) return '';

  let cardEnv = crmConfig.cardProcessingEnvironment;
  let cardEnvParam = crmConfig.cardProcessingEnvParam;
  let cardUrl = crmConfig.cardProcessingUrl;

  if (businessUnitId && cardProcessingEnvironments) {
    const environment = cardProcessingEnvironments.find(
      (env) => env.businessUnitId === businessUnitId
    );

    if (environment) {
      cardEnv = environment.cardProcessingEnvironment;
      cardEnvParam = environment.cardProcessingEnvironmentParam;
      cardUrl = environment.cardProcessingUrl;
    }
  }

  query = {
    ...query,
    u: crmConfig.userContext,
  };

  if (cardEnvParam && cardEnv) {
    query[cardEnvParam] = cardEnv;
  }

  const queryKeys = Object.keys(query);
  const queryKeysLength = queryKeys.length;

  const params = Object.keys(query).reduce<string>((result, key, index) => {
    const isLastKey = index >= queryKeysLength;
    const value = query[key];

    return `${result}${key}=${value}${isLastKey ? '' : '&'}`;
  }, '?');

  // TODO delete fragment after all components redesign
  return cardUrl + page + path + params + fragment;
};

export function diffArray<T extends string | number>(arr1: T[], arr2: T[]) {
  const deleted: T[] = [];
  const added: T[] = [];
  const _arr1 = new Set(arr1);
  const _arr2 = new Set(arr2);
  _arr2.forEach((ele) => _arr1.has(ele) || deleted.push(ele));
  _arr1.forEach((ele) => _arr2.has(ele) || added.push(ele));

  return [added, deleted];
}
